<template>
  <v-card :class="`mx-auto ${!isMobile ? 'mt-4' :''}`" :width="isMobile ? '100%' : '80%'" :outlined="!isMobile">
    <v-card-title class="mb-4">
      <div class="display-1">Demo</div>
      <v-spacer />
    </v-card-title>
    <v-container>
      <v-row justify="center">
        <v-col cols="12" sm="6">
          <div ref="poiMap" class="poi-map"></div>
        </v-col>
        <v-col cols="12" sm="6">
          <v-menu v-model="datesMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="290px">
            <template v-slot:activator="{ on }">
              <v-text-field v-model="dateRangeText" label="Dates" prepend-icon="mdi-calendar" readonly v-on="on" />
            </template>
            <v-date-picker ref="picker" v-model="report.months" min="2018-01" max="2019-12" type="month" no-title range />
          </v-menu>
          <v-slider class="mb-5" v-model="report.buffer" label="Buffer" track-color="dark lighten-5" prepend-icon="mdi-map-marker-circle" min="0" max="100" hide-details>
            <template v-slot:append>
              <div class="text-no-wrap">{{ report.buffer }} miles</div>
            </template>
          </v-slider>
          <v-row v-if="!isMobile">
            <v-spacer />
            <v-btn color="primary" depressed large :loading="pending" @click="runStudy" :disabled="!canRunStudy">Submit</v-btn>
          </v-row>
        </v-col>
      </v-row>
    </v-container>
    <v-card-actions v-if="isMobile">
      <v-btn color="primary" depressed large width="100%" :loading="pending" @click="runStudy" :disabled="!canRunStudy">Submit</v-btn>
    </v-card-actions>
    <v-dialog v-model="queryRunning" hide-overlay persistent fullscreen>
      <v-card class="pending-modal" color="dark" height="100%" width="100%">
        <v-container fluid fill-height>
          <v-row justify="center" fill-height>
            <v-progress-circular color="primary" size="64" indeterminate />
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
    <report-card :data="data" :show.sync="showReportCard" @reset="reset" />
  </v-card>
</template>

<script>
import csv2json from 'csvtojson';
import { v4 as uuidv4 } from 'uuid';
import { isEmpty, defaults } from 'lodash';
import { format as formatDate, parseISO, isBefore, eachMonthOfInterval } from 'date-fns';
import { union, kinks, unkinkPolygon, cleanCoords, buffer as createBuffer } from '@turf/turf';
import { stringify as stringifyGeoJson } from 'wellknown';
import shape2geohash from 'shape2geohash';
import { submitQueryToAthena, waitForExecutionCompletion } from '@/lib/athena';
import Map from '@/lib/map';
import alert from '@/mixins/alert';
import ReportCard from '@/components/ReportCard';

const convertGEOJSON = (feature) => {
  try {
    return {
      ...feature.properties,
      geometry: stringifyGeoJson(feature.geometry)
    };
  } catch (error) {
    if (logger) {
      logger.error(error);
    }
    throw error;
  }
};

const getHashes = async (shape, precision, hashes = {}) => {
  if (precision > 12) {
    throw new Error(`Invalid value for precision: ${precision}`);
  }

  const newHashes = await shape2geohash(shape, {
    precision,
    hashMode: 'intersect',
    minIntersect: 0,
    allowDuplicates: false
  });

  hashes[`geohash${precision}`] = newHashes;
  if (newHashes.length <= 4) { // 4 hashes is worst case and likely too low of a precision
    return getHashes(shape, precision + 2, hashes);
  }
  // if geohash 3 returns 1 hash, it returns empty
  if (hashes.geohash3.length === 0) {
    const [first] = hashes[`geohash${precision}`];
    hashes.geohash3.push(first.slice(0, 3));
  }
  return hashes;
};

const unfurlDates = (dateRange = []) => {
  if (!Array.isArray(dateRange)) {
    dateRange = [dateRange];
  }
  let [start, end] = dateRange.map((d) => parseISO(d));
  end = end || start;
  if (isBefore(end, start)) {
    const start2 = start;
    start = end;
    end = start2;
  }
  let unfurledDates = eachMonthOfInterval({ start, end });
  unfurledDates = unfurledDates.map((d) => formatDate(d, 'yyyyMM'));
  return unfurledDates;
};

export default {
  mixins: [alert],

  components: {
    ReportCard
  },

  data() {
    return {
      formValid: false,
      datesMenu: false,
      map: null,
      pending: false,
      queryRunning: false,
      report: {
        studyArea: {},
        buffer: 0,
        months: []
      },
      data: [],
      showReportCard: false
    };
  },

  mounted() {
    this.initMap();
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mdAndDown;
    },

    dateRangeText() {
      return this.report.months.map((d) => formatDate(parseISO(d), 'MMM dd yyyy')).join(' - ');
    },

    canRunStudy() {
      const { studyArea = {}, months = [] } = this.report;
      return !isEmpty(studyArea) && !isEmpty(months);
    }
  },

  methods: {
    initMap() {
      this.map = new Map(this.$refs.poiMap, {
        eventHandlers: {
          'draw.create': (e) => {
            let [feature] = e.features;
            this.map.draw.deleteAll();
            feature = this.validateShape(feature);
            this.map.draw.add(feature);
            this.report.studyArea = feature;
          },

          'draw.update': (e) => {
            let [feature] = e.features;
            if (feature) {
              this.map.draw.deleteAll();
              feature = this.validateShape(feature);
              this.map.draw.add(feature);
              this.report.studyArea = feature;
            }
          },

          'draw.delete': (e) => {
            this.report.studyArea = {};
          }
        }
      });
    },

    validateShape(shape) {
      try {
        shape = cleanCoords(shape);
        const { features: kinksInShape } = kinks(shape);
        if (kinksInShape.length) {
          shape.geometry = union(...unkinkPolygon(shape).features).geometry;
        }
        return defaults(shape, { id: uuidv4() });
      } catch (error) {
        this.alertError(error);
      }
    },

    async runStudy() {
      try {
        this.pending = true;
        let { studyArea, buffer, months } = this.report;
        const studyAreaWKT = convertGEOJSON(studyArea);
        const bufferAreaWKT = convertGEOJSON(createBuffer(studyArea, buffer, { units: 'miles' }));
        const geohashCovers = await getHashes(studyArea, 3);
        const monthsToRun = unfurlDates(months);

        const query = `
          WITH d1 AS (
            SELECT
              subscriber_id,
              extrapolation_factor,
              point_type,
              date,
              month
            FROM dla3.data
            WHERE month IN (${monthsToRun.map((m) => `'${m}'`).join()})
            AND geo_hash_3 IN (${geohashCovers.geohash3.map((h) => `'${h}'`).join(',')})
            AND point_type = 'EP'
            AND SUBSTR(home_block_id, 1, CASE WHEN SUBSTR(home_block_id, 1, 2) = 'ca' THEN 5 ELSE 2 END) IN (
              SELECT fips
              FROM dla3.states
            )
            AND ST_CONTAINS(ST_GEOMETRYFROMTEXT('${studyAreaWKT.geometry}'), ST_Point(longitude, latitude))
            AND NOT ST_CONTAINS(ST_GEOMETRYFROMTEXT('${bufferAreaWKT.geometry}'), ST_Point(home_long, home_lat))
          ), i AS (
            SELECT
              DISTINCT d1.subscriber_id,
              SUM(DISTINCT d1.extrapolation_factor) AS extrapolation_factor,
              d1.month,
              ARRAY_AGG(DISTINCT DATE_PARSE(CAST(d1.date AS varchar), '%Y%m%d')) AS days_seen
            FROM d1
            GROUP BY d1.subscriber_id, d1.month
          )
          SELECT
            i.month,
            ROUND(SUM(i.extrapolation_factor)) AS extrapolated_devices,
            ROUND(AVG((DATE_DIFF('day', ARRAY_MIN(i.days_seen), ARRAY_MAX(i.days_seen)) + 1)), 2) AS length_of_stay
          FROM i
          GROUP BY i.month
        `;
        const executionId = await submitQueryToAthena(query);
        this.queryRunning = true;
        const { Body } = await waitForExecutionCompletion(executionId);
        this.data = await csv2json().fromString(Body.toString());
        this.showReportCard = true;
      } catch (error) {
        this.alertError(error);
      } finally {
        this.queryRunning = false;
        this.pending = false;
      }
    },

    reset() {
      this.map.draw.deleteAll();
      this.report = {
        studyArea: {},
        buffer: 0,
        months: []
      };
    }
  }
};

</script>

<style lang="scss">
  .poi-map {
    width: 100%;
    height: 500px;
  }

  .mapboxgl-ctrl-bottom-left, .mapboxgl-ctrl-bottom-right {
    display: none;
  }

  .pending-modal {
    opacity: 0.8;
  }
</style>
